import { Badge } from "@hortis/ui/badge";
import { Button } from "@hortis/ui/button";
import { Plus } from "@hortis/ui/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { useEffect, useLayoutEffect, useRef, useState } from "react"; // eslint-disable-line no-restricted-imports
import { useFieldArray, useFormContext } from "react-hook-form";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { useCollectionSiteSettings } from "src/utils/hooks/collection-site-from-url";
import { usePreviousSync } from "src/utils/hooks/previous";
import type { MaterialSchema } from "../add-plant-material-form/schema";
import type {
  CreateAccessionFormSchema,
  CreateAccessionFormSchemaInput,
} from "../schema";
import { MaterialFields } from "./material-fields";
import { MaterialRow } from "./material-row";

export const MaterialSection = ({
  activeMaterial,
  setActiveMaterial,
  siteSlug,
}: {
  activeMaterial: null | number;
  setActiveMaterial: (val: null | number) => void;
  siteSlug: string;
}) => {
  const { materialQualifierFormat } = useCollectionSiteSettings();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  );
  const previousActiveMaterial = usePreviousSync(activeMaterial);
  const form = useFormContext<Partial<CreateAccessionFormSchema>>();
  const taxonOption = form.watch("taxonOption");
  const accessionDate = form.watch("accessionDate");
  const { fields, append, update, remove, insert } =
    useFieldArray<CreateAccessionFormSchemaInput>({
      name: "plantMaterials" as const,
    });
  const [duplicateMaterial, setDuplicateMaterial] = useState<null | number>(
    null,
  );
  const materials = fields as
    | Array<MaterialSchema & { id: string }>
    | undefined
    | null;

  useEffect(() => {
    if (duplicateMaterial != null) {
      setTimeout(() => {
        setDuplicateMaterial(null);
      }, 100);
    }
  }, [duplicateMaterial]);

  useLayoutEffect(() => {
    if (containerRef.current == null) {
      return;
    }

    const observer = new ResizeObserver(() => {
      setContentHeight(containerRef.current?.clientHeight);
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const addMaterialButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (activeMaterial == null && previousActiveMaterial != null) {
      addMaterialButtonRef.current?.focus();
    }
  }, [activeMaterial, previousActiveMaterial]);

  return (
    <div
      className="overflow-clip rounded-lg border border-grey-200 bg-grey-25 shadow-xs transition-all"
      style={{
        height: contentHeight == null ? undefined : contentHeight + 2, // Includes border
        willChange: "height",
      }}
    >
      <div ref={containerRef} className="divide-y divide-grey-200">
        {activeMaterial == null ? (
          <>
            <div className="flex items-center justify-between py-1.5 pl-4 pr-2 text-sm font-medium">
              <div className="flex items-center gap-2 text-sm font-medium">
                Materials
                {materials != null && materials.length > 0 && (
                  <Badge color="grey" className="rounded-md">
                    {materials.length}
                  </Badge>
                )}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    startIcon={Plus}
                    variant="tertiaryGray"
                    size="sm"
                    onClick={() => {
                      setActiveMaterial(fields.length);
                    }}
                    testId="add-material-button"
                    className="gap-1.5"
                    ref={addMaterialButtonRef}
                    onFocusCapture={stopEventPropagation}
                  >
                    Add material
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={0}>
                  Add new material to accession
                </TooltipContent>
              </Tooltip>
            </div>
            {materials != null && materials.length > 0 && (
              <div className="max-h-64 overflow-auto px-1 py-1">
                {materials.map((material, index) => (
                  <MaterialRow
                    qualifierFormat={materialQualifierFormat}
                    updateMaterial={(updates) => {
                      update(index, { ...material, ...updates });
                    }}
                    key={material.id}
                    index={index}
                    material={material}
                    duplicateMaterial={duplicateMaterial === index}
                    onClick={() => {
                      setActiveMaterial(index);
                    }}
                    onDuplicate={() => {
                      insert(index + 1, material);
                      setDuplicateMaterial(index + 1);
                    }}
                    onRemove={() => {
                      remove(index);
                    }}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center px-4 py-[15px] text-sm font-medium">
              <span data-cy="material-form-title">
                {materials?.[activeMaterial] == null
                  ? "Create material"
                  : "Edit material"}
                {taxonOption != null && (
                  <span className="font-normal text-grey-500">
                    {" "}
                    of {taxonOption.scientificName}
                  </span>
                )}
              </span>
            </div>
            <MaterialFields
              siteSlug={siteSlug}
              initialValue={materials?.[activeMaterial]}
              defaultValues={{
                firstPresent: accessionDate,
              }}
              onCancel={() => {
                setActiveMaterial(null);
              }}
              onSubmit={(data) => {
                if (materials?.[activeMaterial] == null) {
                  append(data);
                } else {
                  update(activeMaterial, data);
                }
                setActiveMaterial(null);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};
